import { useContext, useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { UserContext } from "../context/UserContext";
import API_URL from "../config/apiConfig";

const inputClass = "min-h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-accent outline-none focus:border-primary focus:ring-4 focus:ring-primary/10";

export default function RestaurantSettings() {
  const { user } = useContext(UserContext);
  const userId = user?._id;

  const [settings, setSettings] = useState({ tableCount: "", tableReservePrice: "", showMenuButton: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/restaurantSettings/${userId}/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await res.json();
        setSettings({
          tableCount: data.restaurantSettings?.tableCount || "",
          tableReservePrice: data.restaurantSettings?.tableReservePrice || "",
          showMenuButton: data.restaurantSettings?.showMenuButton || false,
        });
      } catch {
        toast.error("دریافت تنظیمات ناموفق بود.");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("tableCount", settings.tableCount);
      formData.append("tableReservePrice", settings.tableReservePrice);
      formData.append("showMenuButton", settings.showMenuButton);

      const res = await fetch(`${API_URL}/api/restaurantSettings/${userId}/settings`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: formData,
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(data?.error?.message || data?.message || "ذخیره تنظیمات ناموفق بود.");
        return;
      }
      toast.success("تنظیمات با موفقیت ذخیره شد");
    } catch {
      toast.error("ذخیره تنظیمات ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-2xl p-4 sm:p-5">
        <div className="h-6 w-28 animate-pulse rounded-lg bg-gray-100" />
        <div className="mt-4 h-48 animate-pulse rounded-xl bg-gray-100" />
      </div>
    );
  }

  return (
    <main className="mx-auto w-full max-w-2xl p-4 sm:p-5 lg:p-6" dir="rtl">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-accent">تنظیمات</h1>
          <p className="mt-1 text-xs leading-5 text-gray-500">تنظیمات مربوط به رزرو میز و نمایش منو در پنل مشتری.</p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-bold text-white transition-colors hover:bg-[#e8731f] disabled:cursor-wait disabled:opacity-60 focus:outline-none focus:ring-4 focus:ring-primary/20"
        >
          {saving ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Check size={15} aria-hidden="true" />}
          {saving ? "در حال ذخیره..." : "ذخیره تنظیمات"}
        </button>
      </header>

      <section className="mt-4 space-y-4 rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-xs font-bold text-gray-600">رزرو میز</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold text-gray-600">تعداد میز</span>
            <input value={settings.tableCount} onChange={(event) => setSettings((prev) => ({ ...prev, tableCount: event.target.value }))} inputMode="numeric" className={inputClass} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold text-gray-600">قیمت رزرو میز (تومان)</span>
            <input value={settings.tableReservePrice} onChange={(event) => setSettings((prev) => ({ ...prev, tableReservePrice: event.target.value }))} inputMode="numeric" className={inputClass} />
          </label>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-accent">نمایش دکمه «مشاهده منو» برای مشتریان</span>
            <input
              type="checkbox"
              checked={settings.showMenuButton}
              onChange={(event) => setSettings((prev) => ({ ...prev, showMenuButton: event.target.checked }))}
              className="h-4 w-4 rounded"
            />
          </label>
        </div>
      </section>
    </main>
  );
}
